import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Lock, PlayCircle, Clock, BarChart3, Layers, Eye } from "lucide-react";
import { api, egp, errMsg, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/providers";
import { useSeo } from "@/lib/seo";
import { FullLoader } from "@/components/site/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SecurePlayer } from "@/components/learn/SecurePlayer";
import { Star } from "@/components/site/Doodles";

const Curriculum = ({ modules, onPreview }) => (
  <Accordion type="multiple" defaultValue={modules.slice(0, 1).map((m) => m.id)} className="space-y-3" data-testid="course-curriculum">
    {modules.map((m, mi) => (
      <AccordionItem key={m.id} value={m.id} className="rounded-2xl bg-white border border-line px-5">
        <AccordionTrigger className="hover:no-underline font-heading text-base font-semibold text-right text-ink">
          <span className="flex items-center gap-3"><span className="font-display text-brand text-xl">{String(mi + 1).padStart(2, "0")}</span>{m.title}<span className="text-xs text-soft font-normal">({m.lessons.length} درس)</span></span>
        </AccordionTrigger>
        <AccordionContent>
          <ul className="space-y-1">
            {m.lessons.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-xl hover:bg-purple-50">
                <span className="flex items-center gap-3 text-sm text-ink">
                  {l.is_preview ? <PlayCircle size={17} className="text-brand" /> : <Lock size={15} className="text-[#B98AE8]" />}
                  {l.title}
                </span>
                <span className="flex items-center gap-3 text-xs text-soft">
                  {l.is_preview && l.has_video && (
                    <button onClick={() => onPreview(l)} data-testid={`preview-lesson-${l.id}`} className="flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-brand hover:bg-purple-200"><Eye size={13} />معاينة</button>
                  )}
                  {l.duration}
                </span>
              </li>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const PriceBox = ({ c }) => {
  const nav = useNavigate();
  const { user } = useAuth();
  const enroll = () => (user ? nav(`/checkout/${c.slug}`) : nav(`/login?next=/checkout/${c.slug}`));
  return (
    <div className="lg:sticky lg:top-24 rounded-3xl bg-white sticker overflow-hidden" data-testid="course-price-box">
      {c.cover_image && <img src={mediaUrl(c.cover_image)} alt={c.title} className="w-full aspect-video object-cover" />}
      <div className="p-6 space-y-5">
        <div className="flex items-end gap-3">
          <span className="font-display text-4xl text-brand" data-testid="course-price">{c.price > 0 ? egp(c.price) : "مجاني"}</span>
          {c.old_price > 0 && <span className="line-through text-soft/70 pb-1">{egp(c.old_price)}</span>}
        </div>
        {c.enrolled ? (
          <Link to={`/learn/${c.id}`} data-testid="course-go-learn-btn" className="btn-pop btn-primary block text-center w-full py-4 rounded-full font-bold">ادخل الكورس</Link>
        ) : c.pending_payment ? (
          <Link to="/dashboard" data-testid="course-pending-btn" className="block text-center w-full py-4 rounded-full bg-purple-50 text-brand font-semibold border border-line">طلب الدفع قيد المراجعة</Link>
        ) : (
          <button onClick={enroll} data-testid="course-enroll-btn" className="btn-pop btn-primary w-full py-4 rounded-full font-bold text-lg">اشترك في الكورس</button>
        )}
        <ul className="space-y-3 text-sm text-soft">
          <li className="flex items-center gap-2"><Layers size={16} className="text-brand" />{c.lessons_count} درس في {c.modules.length} وحدة</li>
          <li className="flex items-center gap-2"><BarChart3 size={16} className="text-brand" />المستوى: {c.level}</li>
          {c.duration_text && <li className="flex items-center gap-2"><Clock size={16} className="text-brand" />{c.duration_text}</li>}
        </ul>
        <p className="text-xs text-soft/80">الدفع عن طريق فودافون كاش أو إنستاباي، والتفعيل بعد مراجعة الإيصال.</p>
      </div>
    </div>
  );
};

export default function CourseDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  useSeo(c?.title, c?.subtitle);
  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e)));
  }, [slug]);
  if (err) return <p className="text-center py-32 text-soft" data-testid="course-not-found">{err}</p>;
  if (!c) return <FullLoader />;
  return (
    <div data-testid="course-detail-page">
      <section className="relative overflow-hidden border-b border-line grid-paper bg-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-5 relative">
            <Star className="absolute -top-6 left-0 w-12 h-12" />
            {!c.published && <span className="inline-block px-3 py-1 rounded-full bg-purple-100 text-brand text-xs">غير منشور — معاينة الأدمن</span>}
            <span className="inline-block px-3 py-1 rounded-full bg-[#FFFFFF] text-[var(--kh-violet)] text-xs font-bold mr-2">{c.category}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-tight" data-testid="course-title">{c.title}</h1>
            {c.subtitle && <p className="text-lg text-soft">{c.subtitle}</p>}
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-14 order-2 lg:order-1">
          {c.what_you_learn?.length > 0 && (
            <section className="rounded-3xl border-2 border-line p-7">
              <h2 className="font-heading text-2xl font-bold text-ink mb-5">هتتعلم إيه؟</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {c.what_you_learn.map((w) => <li key={w} className="flex gap-2 text-ink text-sm"><CheckCircle2 size={18} className="text-brand shrink-0" />{w}</li>)}
              </ul>
            </section>
          )}
          <section>
            <h2 className="font-heading text-2xl font-bold text-ink mb-4">عن الكورس</h2>
            <p className="text-soft leading-loose whitespace-pre-line" data-testid="course-description">{c.description}</p>
          </section>
          <section>
            <h2 className="font-heading text-2xl font-bold text-ink mb-5">محتوى الكورس</h2>
            <Curriculum modules={c.modules} onPreview={setPreview} />
          </section>
          {c.requirements?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-bold text-ink mb-4">المتطلبات</h2>
              <ul className="list-disc pr-6 space-y-2 text-soft">{c.requirements.map((r) => <li key={r}>{r}</li>)}</ul>
            </section>
          )}
        </div>
        <aside className="lg:col-span-4 order-1 lg:order-2"><PriceBox c={c} /></aside>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl bg-white border-line" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-right text-ink">{preview?.title}</DialogTitle>
            <DialogDescription className="text-right">معاينة مجانية</DialogDescription>
          </DialogHeader>
          {preview && <SecurePlayer lessonId={preview.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
